import { useState } from "react";

import { makePostApiCall } from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import PackagingTransferModal from "../../components/packaging/PackagingTransferModal";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import {
  getPackagingBarcode,
  isPackagingItemTransferable,
  mapLookupItemToTransferRecord,
  normalizePackagingSubItemRow,
  parsePackagingTransferResponse,
  transferPackagedItemByBarcode,
} from "../../shared/packagingBulkActions";

export function TraceabilityPackagingBarcodeViewModel(logout, addSnackbar) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [transferInProgress, setTransferInProgress] = useState(false);
  const [itemType, setItemType] = useState("");
  const [item, setItem] = useState(null);
  const [manufacturedProduct, setManufacturedProduct] = useState(null);
  const [histories, setHistories] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [lastSearchedBarcode, setLastSearchedBarcode] = useState("");

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const clearResult = () => {
    setItemType("");
    setItem(null);
    setManufacturedProduct(null);
    setHistories([]);
    setNotFound(false);
  };

  const applyLookupResult = (data) => {
    const rawItem = data?.item ?? null;
    setItemType(data?.carton_type || "");
    setItem(rawItem ? normalizePackagingSubItemRow(rawItem) : null);
    setManufacturedProduct(data?.manufactured_product || null);
    setHistories(Array.isArray(data?.histories) ? data.histories : []);
  };

  const onLookupBarcode = async function (barcode, options = {}) {
    const { preserveSearchState = false } = options;
    const trimmed = (barcode || "").trim();
    if (!trimmed) return;

    setLoading(true);
    if (!preserveSearchState) {
      setHasSearched(true);
      clearResult();
    }
    setLastSearchedBarcode(trimmed);

    const callResult = await makePostApiCall(`fetchPackagingBarcodeHistory`, {
      barcode: trimmed,
    });

    if (callResult.success) {
      applyLookupResult(callResult.data);
    } else if (callResult.errorStatus === 404) {
      setNotFound(true);
      clearResult();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal(
          "Lookup failed",
          callResult.data?.message || "Could not fetch barcode history.",
        );
      }
    }

    setLoading(false);
  };

  const transferLookupItem = async function (lookupItem, lookupItemType, destination) {
    setTransferInProgress(true);

    const callResult = await transferPackagedItemByBarcode(
      [getPackagingBarcode(lookupItem)],
      destination,
    );

    setTransferInProgress(false);

    if (callResult.errorStatus === 401) {
      logout();
      return false;
    }

    if (callResult.success) {
      const { movedCount, failed } = parsePackagingTransferResponse(
        callResult.data,
      );

      setEmptyModelOpen({ open: false, childComponent: null });

      if (movedCount > 0) {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            callResult.data?.message ?? "Item transferred successfully.",
            SnackbarType.Success,
          ),
        );
        if (lastSearchedBarcode) {
          await onLookupBarcode(lastSearchedBarcode, {
            preserveSearchState: true,
          });
        }
        return true;
      }

      const failureMessage =
        failed[0]?.message ?? "Could not transfer this packaging item.";
      showErrorModal("Transfer failed", failureMessage);
      return false;
    }

    const failed = Array.isArray(callResult.data?.failed)
      ? callResult.data.failed
      : [];
    const failureMessage =
      failed[0]?.message ??
      callResult.data?.message ??
      "Could not transfer this packaging item.";

    showErrorModal("Transfer failed", failureMessage);
    return false;
  };

  const openTransferLookupModal = function (lookupItem, lookupItemType) {
    if (!isPackagingItemTransferable(lookupItem, lookupItemType)) {
      showErrorModal(
        "Cannot transfer",
        "Only in-stock items not on a shipment can be transferred.",
      );
      return;
    }

    setEmptyModelOpen({
      childComponent: (
        <PackagingTransferModal
          selectedItems={[
            mapLookupItemToTransferRecord(lookupItem, lookupItemType),
          ]}
          onCancel={() =>
            setEmptyModelOpen({ open: false, childComponent: null })
          }
          onConfirm={(destination) =>
            transferLookupItem(lookupItem, lookupItemType, destination)
          }
          loading={transferInProgress}
        />
      ),
      open: true,
    });
  };

  return {
    loading,
    transferInProgress,
    itemType,
    item,
    manufacturedProduct,
    histories,
    hasSearched,
    notFound,
    onLookupBarcode,
    openTransferLookupModal,
    isPackagingItemTransferable,
    emptyModal,
    setEmptyModelOpen,
  };
}
